import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { SuccessButton, SecondaryButton } from "../../design-system";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { typography, brandColors, surfaces } from "../../design-system/tokens";
import { APP_DOMAIN } from "../../core/utils/config";
import { logoFontStyle } from "../../design-system/brand/logoFont";
import { buildReferralSearchParams, setReferralCoupon } from "../../core/utils/referralCoupon";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";
import { apiFetch } from "../../core/utils/api";

import LandingNavbar from "../landing/LandingNavbar";
import Testimonials from "../landing/Testimonials";
import FinalCTA from "../landing/FinalCTA";
import Footer from "../landing/Footer";
import useGlobalAccessGrantActive from "../landing/useGlobalAccessGrantActive";
import WaveBackground from "../landing/WaveBackground";
import Website from "../../renderer/pages/Website";
import PreviewContext from "../../editor/context/PreviewContext";

import "./landing_v2.css";

const parseFeaturedWebsiteRoute = (route) => {
  const [username = "", domain = ""] = String(route || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  return { username, domain };
};

const buildPreviewWebsite = (website, index) => {
  const { username, domain } = parseFeaturedWebsiteRoute(website?.url);
  if (!username || !domain) return null;

  return {
    ...website,
    previewKey: `${username}/${domain}-${index}`,
    username,
    domain,
  };
};

const LiveWebsitePreview = ({ website }) => {
  const previewContextValue = {
    mode: "responsive",
    viewport: null,
    containerWidth: 1440,
    contentWidth: 1440,
    onNavigate: () => {},
    showFrame: false,
    manualZoom: 1,
  };

  return (
    <div className="landing-v2-preview-iframe-wrapper">
      <PreviewContext.Provider value={previewContextValue}>
        <div style={{ width: "1440px", height: "900px", transform: "scale(0.35)", transformOrigin: "top left" }}>
          <Website username={website.username} domain={website.domain} subdomain="" />
        </div>
      </PreviewContext.Provider>
    </div>
  );
};

const LandingHeroV2 = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const referralSearch = ""; // Can be passed as prop if needed
  const landingLeadSituation = "LANDING_EMAIL";
  
  const [websites, setWebsites] = useState([]);

  useEffect(() => {
    const fetchPublicWebsites = async () => {
      try {
        const response = await apiFetch("/api/websites/public/featured/");
        if (response.ok) {
          const data = await response.json();
          const parsed = (Array.isArray(data) ? data : [])
            .map((w, i) => buildPreviewWebsite(w, i))
            .filter(Boolean);
          setWebsites(parsed.slice(0, 3)); // Take top 3 for the 3D stack
        }
      } catch (err) {
        console.warn("Failed to fetch featured public websites", err);
      }
    };
    fetchPublicWebsites();
  }, []);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      const nextUrl = `${base}${referralSearch ? `&${referralSearch.replace(/^\?/, "")}` : ""}`;
      navigate(nextUrl);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  return (
    <div className="landing-v2-container">
      <WaveBackground variant="inverse" className="absolute inset-0 pointer-events-none opacity-50" />
      <div className="landing-v2-particles"></div>
      
      <div className="landing-v2-hero">
        <div className="landing-v2-text-content">
          <div className="mb-4">
            <span className={`${typography.h1} text-5xl sm:text-6xl lg:text-7xl landing-v2-gradient-text font-extrabold`} style={logoFontStyle}>
              {APP_DOMAIN}
            </span>
          </div>
          <h1 className={`${typography.h2} mb-6 leading-tight transition-colors duration-300`}>
            Create a personal website that makes you <span className="italic text-blue-500 dark:text-blue-400">impossible to ignore</span>
            {isFreeGrantActive ? (
              <span className="text-green-600 dark:text-green-400 font-extrabold whitespace-nowrap block mt-2">{" — for free"}</span>
            ) : null}
          </h1>

          <div className={`mb-10 ${typography.bodyLarge} transition-colors duration-300`}>
            <ul className="space-y-3 text-lg">
              <li className="flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                Get hired faster by top companies
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-purple-500 rounded-full mr-3"></div>
                Impress MBA and Masters admissions
              </li>
              <li className="flex items-center">
                <div className="w-2 h-2 bg-pink-500 rounded-full mr-3"></div>
                Showcase your academic work & stand out
              </li>
            </ul>
          </div>

          <div className="max-w-md">
            {!isHydrated ? (
              <div className="h-32" />
            ) : auth && auth.userId ? (
              <LoggedInCTAButtons username={auth.username} />
            ) : (
              <>
                <form
                  className="flex items-center dark:bg-dark-50/10 bg-dark-900/5 backdrop-blur-md rounded-xl shadow-2xl p-2 mb-6 border dark:border-dark-50/20 border-dark-900/10 transition-colors duration-300"
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isEmailValid) return;
                    handleCreateWebsite();
                  }}
                >
                  <input
                    type="email"
                    aria-label="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className="flex-1 px-4 py-3 bg-transparent dark:text-white text-slate-900 dark:placeholder-gray-400 placeholder-slate-500 focus:outline-none text-base transition-colors duration-300"
                  />
                  <SuccessButton
                    type="submit"
                    disabled={!isEmailValid}
                    aria-label="Get started"
                    rightIcon={<ArrowRightIcon />}
                    className="p-3 rounded-lg shadow-lg hover:shadow-xl transition-all"
                  >
                  </SuccessButton>
                </form>

                <div className="mb-6 flex items-center">
                  <div className={`flex-1 border-t dark:border-dark-50/20 border-dark-900/10 transition-colors duration-300`}></div>
                  <span className={`px-4 text-sm dark:text-gray-400 text-slate-500 uppercase tracking-widest transition-colors duration-300`}>or</span>
                  <div className={`flex-1 border-t dark:border-dark-50/20 border-dark-900/10 transition-colors duration-300`}></div>
                </div>

                <SecondaryButton
                  type="button"
                  onClick={handleSocialLogin}
                  fullWidth={true}
                  leftIcon={<GoogleIcon size="sm" />}
                  variant="alwaysLight"
                  className="dark:bg-dark-50/10 bg-white border dark:border-dark-50/20 border-slate-200 dark:text-white text-slate-900 dark:hover:bg-dark-50/20 hover:bg-slate-50 backdrop-blur-sm transition-all"
                >
                  Sign up with Google
                </SecondaryButton>
              </>
            )}
          </div>
        </div>

        <div className="landing-v2-visual-content hidden lg:block">
          <div className="landing-v2-preview-stack">
            {websites.length > 0 ? websites.map((website, i) => (
              <div 
                key={website.previewKey} 
                className="landing-v2-preview-card"
                style={{ '--tz': `${(3 - i) * 50}px` }}
              >
                <LiveWebsitePreview website={website} />
              </div>
            )) : (
              // Fallback skeleton cards if no websites load
              [1, 2, 3].map((i) => (
                <div 
                  key={i} 
                  className="landing-v2-preview-card dark:bg-dark-800 bg-slate-100 flex items-center justify-center transition-colors duration-300"
                  style={{ '--tz': `${(3 - i) * 50}px` }}
                >
                  <div className="dark:text-dark-600 text-slate-400 animate-pulse">Loading preview...</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const LandingV2 = () => {
  const isFreeGrantActive = useGlobalAccessGrantActive();

  return (
    <div className={`min-h-screen ${surfaces.secondary}`}>
      <LandingNavbar />
      <div className="pt-16">
        <LandingHeroV2 isFreeGrantActive={isFreeGrantActive} />
        <Testimonials />
        <FinalCTA isFreeGrantActive={isFreeGrantActive} />
      </div>
      <Footer />
    </div>
  );
};

export default LandingV2;
